/**
 * ChartMind's steps, their labels, and what "Next" means on each of them.
 *
 * ⚠️ THIS IS ONE SOURCE OF TRUTH ON PURPOSE. The gating used to live inside ChartMindFooter's
 * closure, which was fine while the footer was the only thing that could advance a step. It no
 * longer is: the paired phone shows a Next button too (see usePhoneRecordingRelay's remote), and
 * a phone offering a Next the desktop would refuse is worse than a phone with no Next at all.
 * Both read getStepActions, so they cannot disagree.
 */

export const STEPS = {
  RECORD: "record",
  DIAGNOSIS: "diagnosis",
  // Teaching pearls for the chosen diagnosis. Hidden where the chartmind-pearls prompt is not set up.
  PEARLS: "pearls",
  DIAGNOSTIC_PLAN: "diagnostic_plan",
  CONFIRM_DIAGNOSIS: "confirm_diagnosis",
  TREATMENT: "treatment",
  // The Events note type's first step: the events the note is about.
  EVENTS: "events",
  DISCHARGE: "discharge",
  CHART: "chart",
};

/** Step order for progressive unlocking. */
export const STEP_ORDER = [
  STEPS.RECORD,
  STEPS.DIAGNOSIS,
  STEPS.PEARLS,
  STEPS.DIAGNOSTIC_PLAN,
  STEPS.CONFIRM_DIAGNOSIS,
  STEPS.TREATMENT,
  STEPS.EVENTS,
  STEPS.DISCHARGE,
  STEPS.CHART,
];

/**
 * The Events note type is its own short flow. Event takes Record's place: there is no narrative,
 * and the Event step carries the setup rows Record has.
 */
export const EVENTS_NOTE_STEP_ORDER = [STEPS.EVENTS, STEPS.DIAGNOSIS, STEPS.TREATMENT, STEPS.CHART];

/**
 * What each step is called, for the stepper and for the phone's "on your computer" line.
 *
 * [BETA-CM0427] In CM0427 mode, Treatment is relabeled "Plan" (the diagnostic Plan step is
 * hidden), so the label depends on the mode rather than being a flat constant.
 */
export const getStepLabels = (cm0427Mode = false, inpatient = false, eventsNote = false) => ({
  [STEPS.RECORD]: inpatient ? "Narrative" : "Recording",
  [STEPS.DIAGNOSIS]: eventsNote ? "Differential" : "Diagnosis",
  [STEPS.PEARLS]: "Pearls",
  [STEPS.DIAGNOSTIC_PLAN]: "Plan",
  [STEPS.CONFIRM_DIAGNOSIS]: "Confirm",
  [STEPS.TREATMENT]: eventsNote
    ? "Plan"
    : inpatient
      ? "Daily Plan"
      : cm0427Mode
        ? "Plan"
        : "Treatment",
  [STEPS.EVENTS]: "Event",
  [STEPS.DISCHARGE]: "Discharge",
  [STEPS.CHART]: "Chart",
});

/**
 * The steps the stepper shows. Events exists only on the Events note type, and inpatient skips
 * the diagnostic Plan and Confirm steps the same way CM0427 does. Pearls drops out where its
 * prompt is not set up.
 */
export const getVisibleStepOrder = (
  cm0427Mode = false,
  inpatient = false,
  pearlsAvailable = true,
  eventsNote = false,
) => {
  if (eventsNote) return EVENTS_NOTE_STEP_ORDER;
  return STEP_ORDER.filter((step) => {
    if (step === STEPS.EVENTS) return false;
    if (step === STEPS.PEARLS) return pearlsAvailable;
    if (step === STEPS.DIAGNOSTIC_PLAN || step === STEPS.CONFIRM_DIAGNOSIS) {
      return !cm0427Mode && !inpatient;
    }
    return true;
  });
};

/**
 * The footer's button configuration for a step, plus why Next is unavailable when it is.
 *
 * ⚠️ nextBlockedReason IS FOR THE PHONE, and it exists because a disabled button is only honest
 * when you can see the screen that explains it. On the desktop the greyed-out Next sits under
 * the diagnoses you have not picked yet, so the reason is on screen. On a phone in a corridor
 * there is nothing around it, and "Next is greyed out" with no cause reads as a bug. It says
 * what to do rather than what is wrong, and it names the computer, because that is where the
 * clinician has to do it.
 *
 * @param {Object} params
 * @param {string} params.currentStep
 * @param {string} params.transcript
 * @param {number} params.selectedDiagnosesCount
 * @param {number} params.selectedConfirmedDiagnosesCount
 * @param {boolean} params.clinicianImpressionRecorded  whether the clinician has given their
 *   own diagnosis yet — until they have, the Diagnosis step shows the gate and no differential
 * @param {boolean} params.cm0427Mode
 * @param {boolean} params.inpatient  skips the diagnostic Plan and Confirm steps
 * @param {boolean} params.eventsNote  the Events note type: Event, Differential, Plan, Chart
 * @param {number} params.eventsCount  events entered, which gate leaving the Event step
 * @param {boolean} params.pearlsAvailable  false skips the Pearls step
 * @param {Object} params.handlers  the onContinueTo* callbacks, by name
 */
export const getStepActions = ({
  currentStep,
  transcript,
  selectedDiagnosesCount = 0,
  selectedConfirmedDiagnosesCount = 0,
  clinicianImpressionRecorded = true,
  cm0427Mode = false,
  inpatient = false,
  eventsNote = false,
  eventsCount = 0,
  pearlsAvailable = true,
  handlers = {},
}) => {
  // Inpatient follows the CM0427 flow: no diagnostic Plan or Confirm step.
  const shortFlow = cm0427Mode || inpatient;
  const {
    onContinueToDiagnosis,
    onContinueToPearls,
    onContinueToPlan,
    onContinueToConfirm,
    onContinueToTreatment,
    onContinueToDischarge,
    onGenerateChart,
    onSkipToChart,
  } = handlers;

  // [BETA-CM0427] With Confirm hidden, the later steps gate on selected (preliminary)
  // diagnoses instead of confirmed ones — there is no Confirm step to produce them.
  const diagnosesGateCount = shortFlow
    ? selectedDiagnosesCount
    : selectedConfirmedDiagnosesCount;
  const diagnosesGateReason = shortFlow
    ? "Please choose diagnoses on your computer first."
    : "Please confirm diagnoses on your computer first.";

  switch (currentStep) {
    case STEPS.RECORD: {
      // The stepper already allows jumping straight to Chart, but users were not finding it.
      // Surface it as a secondary action here. Same transcript gate as Next: the note is built
      // from the transcript alone, which generateNote supports (diagnoses are optional context).
      const noUsableTranscript = !transcript || transcript.length < 50;
      return {
        showBack: false,
        showNext: true,
        nextLabel: "Choose Diagnoses",
        nextDisabled: noUsableTranscript,
        nextBlockedReason: noUsableTranscript
          ? "Please record the encounter first."
          : "",
        onNext: onContinueToDiagnosis,
        secondaryLabel: "Skip to Chart",
        secondaryDisabled: noUsableTranscript,
        onSecondary: onSkipToChart,
      };
    }

    case STEPS.DIAGNOSIS:
      return {
        showBack: true,
        showNext: true,
        // [BETA-CM0427] In CM0427 mode, "Plan" is the renamed Treatment step — skip diagnostic
        // plan + confirm.
        nextLabel: pearlsAvailable && !eventsNote ? "Teaching Pearls" : "Review Plan",
        nextDisabled: selectedDiagnosesCount === 0,
        // Two different dead ends, and they need different sentences. Before the gate is
        // answered there is no list to choose from, so telling the clinician in the corridor to
        // pick a diagnosis would send them back to a screen that is not showing any.
        nextBlockedReason:
          selectedDiagnosesCount > 0
            ? ""
            : clinicianImpressionRecorded
              ? "Please choose diagnoses on your computer first."
              : "Please enter your own diagnosis on your computer first.",
        onNext: eventsNote
          ? onContinueToTreatment
          : pearlsAvailable
            ? onContinueToPearls
            : shortFlow
              ? onContinueToTreatment
              : onContinueToPlan,
      };

    case STEPS.PEARLS:
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Review Plan",
        nextDisabled: selectedDiagnosesCount === 0,
        nextBlockedReason:
          selectedDiagnosesCount === 0 ? "Please choose diagnoses on your computer first." : "",
        onNext: shortFlow ? onContinueToTreatment : onContinueToPlan,
      };

    case STEPS.DIAGNOSTIC_PLAN:
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Confirm Diagnosis",
        nextDisabled: selectedDiagnosesCount === 0,
        nextBlockedReason:
          selectedDiagnosesCount === 0
            ? "Please choose diagnoses on your computer first."
            : "",
        onNext: onContinueToConfirm,
      };

    case STEPS.CONFIRM_DIAGNOSIS:
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Review Treatments",
        nextDisabled: selectedConfirmedDiagnosesCount === 0,
        nextBlockedReason:
          selectedConfirmedDiagnosesCount === 0
            ? "Please confirm diagnoses on your computer first."
            : "",
        onNext: onContinueToTreatment,
      };

    case STEPS.TREATMENT:
      return {
        showBack: true,
        showNext: true,
        // The Events note has no Discharge step: the plan goes straight into the note.
        nextLabel: eventsNote ? "Generate Note" : "Review Discharge",
        nextDisabled: diagnosesGateCount === 0,
        nextBlockedReason: diagnosesGateCount === 0 ? diagnosesGateReason : "",
        onNext: eventsNote ? onGenerateChart : onContinueToDischarge,
      };

    // The Events note type's first step, in place of Record. The events are the note's only
    // source, so leaving it needs at least one, the way the Record step needs a transcript.
    case STEPS.EVENTS: {
      const noEvents = eventsCount < 1;
      return {
        showBack: false,
        showNext: true,
        nextLabel: "Choose Diagnoses",
        nextDisabled: noEvents,
        nextBlockedReason: noEvents ? "Please add an event on your computer first." : "",
        onNext: onContinueToDiagnosis,
        secondaryLabel: "Skip to Chart",
        secondaryDisabled: noEvents,
        onSecondary: onSkipToChart,
      };
    }

    case STEPS.DISCHARGE:
      return {
        showBack: true,
        showNext: true,
        nextLabel: "Generate Note",
        nextDisabled: diagnosesGateCount === 0,
        nextBlockedReason: diagnosesGateCount === 0 ? diagnosesGateReason : "",
        onNext: onGenerateChart,
      };

    case STEPS.CHART:
      // The end of the flow. Nothing to advance to, which is why the phone shows no button
      // here rather than a disabled one: there is no action being withheld.
      return { showBack: true, showNext: false };

    default:
      return { showBack: false, showNext: false };
  }
};
